import { useEffect, useRef } from "react";
import { useAuthMessages } from "@/context/auth/AuthContext";
import { usePendingProductWorkflowsStore } from "@/context/pending-product-workflows/PendingProductWorkflowsContext";

/**
 * Tipo del evento que trd-core publica al octopus cuando termina (success o
 * failure) un workflow de catalog con id determinístico:
 *
 * - `productAttributeRebuildWorkflow` (`attr-rebuild-${biz}-${product}`)
 * - `productAttributeUpdateWorkflow` (`attr-update-${biz}-${product}`)
 *
 * Ver: trd-core/src/octopus/notification-type.enum.ts → `CatalogAttributeWorkflowCompleted`.
 */
const CATALOG_WORKFLOW_EVENT = "catalog.attribute_workflow.completed";

/**
 * Listener global que recibe el evento del socket cuando un workflow de
 * catalog con id determinístico termina, y libera el guard del producto
 * en el `PendingProductWorkflowsStore`.
 *
 * Al liberar el guard, el store desencola la próxima acción pendiente para
 * ese producto (si hay) y la ejecuta — el efecto es que el usuario puede
 * clickear varias veces sin esperar y todo se aplica en orden de forma
 * transparente.
 *
 * Montar UNA SOLA VEZ en `routes.tsx` junto al otro listener.
 */
export const usePendingProductWorkflowsSocketListener = () => {
  const messages = useAuthMessages();
  const end = usePendingProductWorkflowsStore.use.end();

  // Deduplicación: cada notificación trae un `notificationId` único.
  const processedIds = useRef<Set<string>>(new Set());

  useEffect(() => {
    if (!messages || messages.length === 0) return;

    for (const message of messages) {
      const notificationId = message?.notificationId;
      if (!notificationId || processedIds.current.has(notificationId)) continue;

      const notification = message?.notification;
      if (notification?.type !== CATALOG_WORKFLOW_EVENT) continue;

      const productId = notification?.payload?.productId;
      if (!productId || typeof productId !== "string") {
        // Payload mal formado — log pero no dispares end() a ciegas.
        console.warn(
          "[catalog-workflow-listener] event without productId payload",
          notification?.payload
        );
        continue;
      }

      processedIds.current.add(notificationId);

      end(productId);
    }
  }, [messages, end]);
};
